import Phaser from 'phaser';
import { ANIMALS } from './animals/definitions';
import { animalTextureKey, collisionPartsForAnimal, createAnimal, enableAnimalCollisions } from './animals/factory';
import { animalSvgDataUri } from './animals/svg';
import type { AnimalDefinition } from './animals/types';
import { GameAudio } from './audio/GameAudio';
import { createBestRecordStore } from './logic/bestScoreStore';
import { createAnimalBagSelector } from './logic/animalBagSelector';
import {
  advanceCameraTransition,
  beginCameraTransition,
  cameraFrameForZoom,
  cameraTargetZoomForPlacedBottom,
  cameraZoomToFitWorldWidth,
  cameraWorldViewBoundsAtMinimumZoom,
  type CameraFrame,
  type CameraTransition,
  type WorldRect,
} from './logic/cameraPolicy';
import { ContactGraph, normalizeCollisionParents } from './logic/contactGraph';
import {
  animalAnimalMaterialMultiplierFromSearch,
  animalSupportFrictionFromSearch,
  animalSupportFrictionStaticFromSearch,
  contactMaterialOverrideEnabled,
  installContactMaterialOverride,
  type ContactMaterialPair,
} from './logic/contactMaterial';
import type { GamePhase } from './logic/gameState';
import {
  drawableHitboxParts,
  hitboxDebugEnabled,
  nextHitboxMode,
  selectHitboxTargets,
  type HitboxMode,
} from './logic/hitboxDebug';
import {
  bodyPositionForPreservedAimAnchor,
  centerOfMassAimOffset,
  centerOfMassDebugPoint,
  preservedAimOrigin,
} from './animals/centerOfMass';
import {
  advanceHeldKeyRepeat,
  createHeldKeyRepeatState,
  type HeldKeyRepeatState,
} from './logic/heldKeyRepeat';
import { detectSupportPenetration, isOut, updateEscapeMetadata, type OutMetadata } from './logic/out';
import { hasFinitePhysicsState } from './logic/physicsInvariant';
import { indirectImpactRetention } from './logic/impactTransmission';
import {
  snapshotPhysicsTelemetry,
  type PhysicsPairTelemetryInput,
  type PhysicsTelemetrySnapshot,
} from './logic/physicsTelemetry';
import {
  createPlacementTracker,
  shouldWakeUnsupportedDropped,
  updatePlacementContact,
  type PlacementTracker,
} from './logic/placement';
import { gravityDirectionVelocityY } from './logic/terminalVelocity';
import { initialDebugTerminalVelocity } from './logic/terminalVelocityDebug';
import { calculatePlacementScore, placedStackBottom } from './logic/score';
import { measureStack, updateRunMeters } from './logic/measurement';
import { createGrassTeeth } from './logic/supportGraphic';
import { createCloudContour, type CloudVariant } from './logic/cloudPath';
import { clampAimScreenX, spawnLineCssFrame } from './logic/screenAnchors';
import { activePhysicsIsolationPreset, type PhysicsIsolationPreset } from './physicsIsolationPresets';
import {
  SUPPORT_BODY_OPTIONS,
  SUPPORT_HEIGHT,
  SUPPORT_WIDTH,
  SUPPORT_X,
  SUPPORT_Y,
} from './supportGeometry';
import { AimingPointerTracker } from './logic/responsiveInput';
import {
  DESKTOP_VIEWPORT_METRICS,
  layoutModeForViewport,
  MOBILE_AIMING_EDGE_GAP,
  renderPixelRatioForDevice,
  viewportMetricsForStage,
  type ViewportMetrics,
} from './logic/viewportMetrics';
import { GameUi } from './ui/screens';
import { GAME_HEIGHT, GAME_WIDTH } from './worldDimensions';

const OUT_LINE_Y = -26;
const BACKDROP_GRADIENT_LEFT = -160;
const BACKDROP_GRADIENT_RIGHT = GAME_WIDTH + 160;
const BACKDROP_GRADIENT_TOP = -100;
const BACKDROP_GRADIENT_BOTTOM = GAME_HEIGHT + 220;
const BACKDROP_SAFETY_MARGIN = 64;
const DEFAULT_CURRENT_FALL_TERMINAL_SPEED = 6.5;
const PLACED_FRICTION_AIR_MULTIPLIER = 2;
const PLACED_SLEEP_THRESHOLD = 30;
const CLOUD_PLACEMENTS: Array<{
  variant: CloudVariant;
  centerX: number;
  centerY: number;
  scale: number;
}> = [
  { variant: 'A', centerX: 145, centerY: 245, scale: 0.78 },
  { variant: 'C', centerX: 820, centerY: 325, scale: 0.7 },
  { variant: 'B', centerX: 255, centerY: 470, scale: 0.68 },
  { variant: 'C', centerX: 760, centerY: 560, scale: 0.62 },
];
const MatterRuntime = (Phaser.Physics.Matter as unknown as {
  Matter: {
    version: string;
    after(path: string, callback: (pair: ContactMaterialPair) => void): void;
    Sleeping: { set(body: MatterJS.BodyType, sleeping: boolean): void };
    Events: {
      on(target: object, eventName: string, callback: () => void): void;
      off(target: object, eventName: string, callback: () => void): void;
    };
  };
}).Matter;

installContactMaterialOverride(MatterRuntime, {
  enabled: contactMaterialOverrideEnabled(window.location.search),
  animalAnimalMaterialMultiplier: animalAnimalMaterialMultiplierFromSearch(window.location.search),
  animalSupportFriction: animalSupportFrictionFromSearch(window.location.search),
  animalSupportFrictionStatic: animalSupportFrictionStaticFromSearch(window.location.search),
});

interface AnimalRuntime {
  image: Phaser.Physics.Matter.Image;
  definition: AnimalDefinition;
  normalFrictionAir: number;
  contactedPartIds: Set<string>;
  activeStackPartContacts: Map<string, number>;
  dropped: boolean;
  placed: boolean;
  placement: PlacementTracker;
  outMetadata: OutMetadata;
  hasEverContactedStack: boolean;
  previousBounds: { minX: number; maxX: number; minY: number; maxY: number };
}

interface DebugSnapshot {
  phase: GamePhase;
  score: number;
  best: number;
  currentRunMeters: number;
  bestPoint: number;
  bestMeters: number;
  gravityY: number;
  physicsPreset: PhysicsIsolationPreset;
  animals: Array<{ id: string; x: number; y: number; bodyX: number; bodyY: number; bodyAngle: number; velocityY: number; speed: number; angularSpeed: number; frictionAir: number; sleepThreshold: number; sleeping: boolean; mass: number; inertia: number; boundsMinY: number; boundsMaxY: number; collisionMask: number; originX: number; originY: number; displayWidth: number; displayHeight: number; angle: number; visible: boolean; textureKey: string; contactedPartIds: string[]; activeContactCount: number; placed: boolean; dropped: boolean; supportConnected: boolean; placementConnectedDurationMs: number; pendingPlacement: boolean }>;
  gameOverReason: string | null;
  runtimeFailureReason: string | null;
  placementTelemetry: PlacementTelemetry[];
  cameraZoom: number;
  cameraCenterY: number;
  cameraTargetZoom: number;
  cameraTargetCenterY: number;
  aimingCameraEnvelope: { lowerExtentWorld: number; topExtentWorld: number; heightWorld: number };
  aimingContactCount: number;
  viewport: ViewportMetrics;
  renderer: { type: 'WEBGL' | 'CANVAS' | 'UNKNOWN'; mipmapFilter: string };
  backdropBounds: WorldRect;
  hitbox: {
    mode: HitboxMode;
    animalIds: string[];
    partLabels: string[];
    includesSupport: boolean;
    centerOfMassPoints: Array<{
      animalId: string;
      mode: 'auto' | 'authored';
      x: number;
      y: number;
    }>;
  };
}

interface PlacementTelemetry {
  id: string;
  contactPartIds: string[];
  connectedDurationMs: number;
  speed: number;
  angularSpeed: number;
  sleeping: boolean;
  supportConnected: boolean;
}

interface CompoundInspection {
  id: string;
  partCount: number;
  expectedPartCount: number;
  mass: number;
  inertia: number;
  bounds: { minX: number; maxX: number; minY: number; maxY: number };
  maximumLowerExtent: number;
  aligned: boolean;
  textureSourceWidth: number;
  textureSourceHeight: number;
  frameWidth: number;
  frameHeight: number;
  textureFilter: 'LINEAR' | 'NEAREST' | 'UNKNOWN';
  partLabels: string[];
  partBounds: Array<{ label: string; minX: number; maxX: number; minY: number; maxY: number }>;
}

declare global {
  interface Window {
    __REVERSE_ANIMAL_TOWER__?: {
      snapshot(): DebugSnapshot;
      setAim(x: number, angle: number): void;
      drop(): void;
      setCurrentAngularVelocityForTest(velocity: number): void;
      textureExistsForTest(key: string): boolean;
      setCurrentParentBoundsMaxYForTest(maxY: number): void;
      currentOutForTest(): boolean;
      destabilizePlaced(): void;
      forceSleepCurrent(): void;
      forceSleepPlaced(): void;
      forceSweptSupportCrossing(): void;
      setPhysicsPausedForTest(paused: boolean): void;
      disconnectSupportGraphForTest(): void;
      setPlacedCameraBottomForTest(bottomY: number): void;
      launchPlacedThroughAiming(): void;
      inspectRoster(): CompoundInspection[];
      physicsTelemetry(): PhysicsTelemetrySnapshot | null;
    };
  }
}

export class GameScene extends Phaser.Scene {
  private ui!: GameUi;
  private readonly audio = new GameAudio();
  private readonly bestStore = createBestRecordStore(window.localStorage);
  private bestPoint = 0;
  private bestMeters = 0;
  private currentRunMeters = 0;
  private score = 0;
  private phase: GamePhase = 'title';
  private support!: MatterJS.BodyType;
  private readonly contacts = new ContactGraph();
  private readonly animals = new Map<number, AnimalRuntime>();
  private current: AnimalRuntime | null = null;
  private latestAnimalBodyId: number | null = null;
  private previousBottomY = SUPPORT_Y + SUPPORT_HEIGHT / 2;
  private animalCursor = 0;
  private readonly animalSelector = createAnimalBagSelector(ANIMALS, () => {
    const params = new URLSearchParams(window.location.search);
    return params.has('e2e') && params.get('bagRandom') === 'fixed' ? 0.999 : Math.random();
  });
  private keys!: Record<'left' | 'right' | 'rotateLeft' | 'rotateRight' | 'drop', Phaser.Input.Keyboard.Key>;
  private rotateLeftRepeat: HeldKeyRepeatState = createHeldKeyRepeatState();
  private rotateRightRepeat: HeldKeyRepeatState = createHeldKeyRepeatState();
  private mobileRotateLeftRepeat: HeldKeyRepeatState = createHeldKeyRepeatState();
  private mobileRotateRightRepeat: HeldKeyRepeatState = createHeldKeyRepeatState();
  private mobileRotateLeftHeld = false;
  private mobileRotateRightHeld = false;
  private suppressSupportGraphForTest = false;
  private gameOverReason: string | null = null;
  private runtimeFailureReason: string | null = null;
  private readonly placementTelemetry: PlacementTelemetry[] = [];
  private cameraFrame: CameraFrame = cameraFrameForZoom(1, SUPPORT_Y + SUPPORT_HEIGHT / 2);
  private cameraTargetZoom = 1;
  private cameraTransition: CameraTransition = beginCameraTransition(1, 1);
  private cameraPlacedBottomY = SUPPORT_Y + SUPPORT_HEIGHT / 2;
  private startOverviewActive = false;
  private viewport: ViewportMetrics = DESKTOP_VIEWPORT_METRICS;
  private renderPixelRatio = renderPixelRatioForDevice(window.devicePixelRatio);
  private maximumRosterAimingLowerExtent = 0;
  private aimingCameraEnvelope = { lowerExtentWorld: 0, topExtentWorld: 0, heightWorld: 0 };
  private aimScreenX = DESKTOP_VIEWPORT_METRICS.canvasCenterX;
  private readonly aimingPointer = new AimingPointerTracker();
  private stageResizeObserver: ResizeObserver | null = null;
  private viewportResizeFrame: number | null = null;
  private backdrop!: Phaser.GameObjects.Graphics;
  private spawnLineGraphic!: Phaser.GameObjects.Graphics;
  private hitboxOverlay: Phaser.GameObjects.Graphics | null = null;
  private hitboxMode: HitboxMode = 'OFF';
  private lastHitboxToggleTime = Number.NEGATIVE_INFINITY;
  private readonly hitboxDebugActive = hitboxDebugEnabled(window.location.search);
  private currentFallTerminalSpeed = initialDebugTerminalVelocity(
    window.location.search,
    DEFAULT_CURRENT_FALL_TERMINAL_SPEED,
  );
  private readonly currentFallTerminalVelocityEnabled = (() => {
    const params = new URLSearchParams(window.location.search);
    return !(params.has('e2e') && params.get('fallSpeed') === 'baseline');
  })();
  private backdropBounds: WorldRect = {
    left: BACKDROP_GRADIENT_LEFT,
    right: BACKDROP_GRADIENT_RIGHT,
    top: BACKDROP_GRADIENT_TOP,
    bottom: BACKDROP_GRADIENT_BOTTOM,
  };
  private readonly scheduleViewportRefresh = (): void => {
    if (this.viewportResizeFrame !== null) return;
    this.viewportResizeFrame = requestAnimationFrame(() => {
      this.viewportResizeFrame = null;
      this.refreshViewLayout();
    });
  };
  private readonly toggleHitboxMode = (event: KeyboardEvent): void => {
    if (!this.hitboxDebugActive || event.repeat || event.timeStamp - this.lastHitboxToggleTime < 30) return;
    this.lastHitboxToggleTime = event.timeStamp;
    this.hitboxMode = nextHitboxMode(this.hitboxMode);
    this.ui.setHitboxDebugMode(this.hitboxMode);
    this.drawHitboxOverlay();
  };
  constructor() {
    super('game');
  }

  preload(): void {
    for (const animal of ANIMALS) {
      if (animal.texture?.kind === 'atlas') {
        this.load.atlas(animalTextureKey(animal), animal.texture.imagePath, animal.texture.atlasPath);
      } else if (animal.texture?.kind === 'image') {
        this.load.image(animalTextureKey(animal), animal.texture.path);
      } else {
        this.load.svg(animalTextureKey(animal), animalSvgDataUri(animal), { width: animal.width, height: animal.height });
      }
    }
  }

  create(): void {
    const uiRoot = document.querySelector<HTMLElement>('#game-ui');
    const hudRoot = document.querySelector<HTMLElement>('#game-hud');
    const controlsRoot = document.querySelector<HTMLElement>('#mobile-controls');
    if (!uiRoot || !hudRoot || !controlsRoot) throw new Error('Missing game UI roots');
    this.ui = new GameUi(uiRoot, hudRoot, controlsRoot);
    const records = this.bestStore.load();
    this.bestPoint = records.bestPoint;
    this.bestMeters = records.bestMeters;
    this.createBackdrop();
    this.createSupport();
    this.spawnLineGraphic = this.add.graphics().setDepth(5);
    if (this.hitboxDebugActive) this.hitboxOverlay = this.add.graphics().setDepth(100);
    if (new URLSearchParams(window.location.search).has('colliders')) {
      this.matter.world.createDebugGraphic();
      this.matter.world.debugGraphic.setDepth(1_000);
    }
    const rosterAimingEnvelope = this.measureMaximumRosterAimingEnvelope();
    this.maximumRosterAimingLowerExtent = rosterAimingEnvelope.lowerExtentWorld;
    this.aimingCameraEnvelope = {
      lowerExtentWorld: rosterAimingEnvelope.lowerExtentWorld,
      topExtentWorld: rosterAimingEnvelope.topExtentWorld,
      heightWorld: rosterAimingEnvelope.heightWorld,
    };
    this.createInput();
    this.createCollisionEvents();
    this.createViewportResizeObserver();
    this.installDebugApi();
    this.ui.showTitle(records, () => { void this.startGame(); });
    this.refreshViewLayout();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
  }

  update(_time: number, delta: number): void {
    if (this.phase !== 'title' && this.phase !== 'gameOver' && this.phase !== 'runtimeFailure') {
      this.updatePlayCamera(delta);
      this.updateAim(delta);
      this.updatePhysicsState(delta);
    }
    this.drawHitboxOverlay();
    this.updateDebugAttribute();
  }

  private createBackdrop(): void {
    this.backdrop = this.add.graphics();
    this.redrawBackdropForViewport(SUPPORT_Y + SUPPORT_HEIGHT / 2);
  }

  private redrawBackdropForViewport(supportAnchorWorldY: number): void {
    const required = cameraWorldViewBoundsAtMinimumZoom(
      SUPPORT_X,
      supportAnchorWorldY,
      this.viewport,
      BACKDROP_SAFETY_MARGIN,
    );
    const left = Math.min(BACKDROP_GRADIENT_LEFT, required.left);
    const right = Math.max(BACKDROP_GRADIENT_RIGHT, required.right);
    const top = Math.min(BACKDROP_GRADIENT_TOP, required.top);
    const bottom = Math.max(BACKDROP_GRADIENT_BOTTOM, required.bottom);
    this.backdropBounds = { left, right, top, bottom };

    this.backdrop.clear();
    this.backdrop.fillStyle(0xffffff, 0.72);
    this.backdrop.lineStyle(2, 0x2d2a28, 0.68);
    for (const cloud of CLOUD_PLACEMENTS) {
      const contour = createCloudContour(
        cloud.variant,
        cloud.centerX,
        cloud.centerY,
        cloud.scale,
      ).map(({ x, y }) => new Phaser.Math.Vector2(x, y));
      this.backdrop.fillPoints(contour, true);
      this.backdrop.strokePoints(contour, true, true);
    }
  }

  private createSupport(): void {
    const leftX = SUPPORT_X - SUPPORT_WIDTH / 2;
    const topY = SUPPORT_Y - SUPPORT_HEIGHT / 2;
    const bottomY = SUPPORT_Y + SUPPORT_HEIGHT / 2;
    const grassTopY = bottomY - 7;
    const supportGraphic = this.add.graphics();
    supportGraphic.fillStyle(0x785339, 1);
    supportGraphic.fillRoundedRect(leftX, topY, SUPPORT_WIDTH, SUPPORT_HEIGHT, 4);
    supportGraphic.fillStyle(0x6da443, 1);
    supportGraphic.fillRect(leftX + 1, grassTopY, SUPPORT_WIDTH - 2, bottomY - grassTopY - 1);
    for (const tooth of createGrassTeeth(leftX + 1, SUPPORT_WIDTH - 2, 16)) {
      supportGraphic.fillTriangle(tooth.leftX, grassTopY, tooth.apexX, grassTopY - 7, tooth.rightX, grassTopY);
    }
    supportGraphic.lineStyle(2, 0x5a493d, 0.82);
    supportGraphic.strokeRoundedRect(leftX, topY, SUPPORT_WIDTH, SUPPORT_HEIGHT, 4);
    this.support = this.matter.add.rectangle(
      SUPPORT_X,
      SUPPORT_Y,
      SUPPORT_WIDTH,
      SUPPORT_HEIGHT,
      SUPPORT_BODY_OPTIONS,
    );
  }

  private createInput(): void {
    if (!this.input.keyboard) throw new Error('Keyboard input unavailable');
    this.keys = {
      left: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A),
      right: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D),
      rotateLeft: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Q),
      rotateRight: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.E),
      drop: this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE),
    };
    if (this.hitboxDebugActive) this.input.keyboard.on('keydown-H', this.toggleHitboxMode);
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.phase === 'aiming' && this.current
        && this.aimingPointer.acceptsMove(this.viewport.layoutMode, pointer.id, pointer.isDown)) {
        this.setAimScreenX(pointer.x / this.renderPixelRatio);
      }
    });
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (this.viewport.layoutMode === 'desktop') {
        this.dropCurrent();
        return;
      }
      this.aimingPointer.begin(this.viewport.layoutMode, pointer.id);
      if (this.phase === 'aiming' && this.current) this.setAimScreenX(pointer.x / this.renderPixelRatio);
    });
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => this.aimingPointer.end(pointer.id));
    this.input.on('pointerupoutside', (pointer: Phaser.Input.Pointer) => this.aimingPointer.end(pointer.id));
    this.input.on('gameout', () => this.aimingPointer.cancel());
  }

  private createViewportResizeObserver(): void {
    const stage = document.querySelector<HTMLElement>('#game-stage');
    if (!stage) throw new Error('Missing #game-stage');
    this.stageResizeObserver = new ResizeObserver(() => this.scheduleViewportRefresh());
    this.stageResizeObserver.observe(stage);
    window.visualViewport?.addEventListener('resize', this.scheduleViewportRefresh);
  }

  private syncSpawnLineLayout(): void {
    this.ui.updateSpawnLineLayout(spawnLineCssFrame(this.game.canvas.getBoundingClientRect(), this.viewport));
    this.syncSpawnLineGraphic();
  }

  private syncSpawnLineGraphic(): void {
    this.spawnLineGraphic.clear();
    const visible = this.phase !== 'title' && this.phase !== 'gameOver' && this.phase !== 'runtimeFailure';
    this.spawnLineGraphic.setVisible(visible);
    if (!visible) return;
    const left = this.cameras.main.getWorldPoint(0, this.viewport.spawnScreenY * this.renderPixelRatio);
    const right = this.cameras.main.getWorldPoint(
      this.viewport.stageWidth * this.renderPixelRatio,
      this.viewport.spawnScreenY * this.renderPixelRatio,
    );
    this.spawnLineGraphic.lineStyle(2 / this.cameraFrame.zoom, 0x2d5668, 0.25);
    this.spawnLineGraphic.lineBetween(left.x, left.y, right.x, right.y);
  }

  private createCollisionEvents(): void {
    const onBeforeSolve = () => this.applyCurrentFallTerminalVelocity();
    const onStart = (event: Phaser.Physics.Matter.Events.CollisionStartEvent) => {
      for (const pair of event.pairs) {
        const [a, b] = normalizeCollisionParents(pair.collision);
        if (!this.suppressSupportGraphForTest || (a !== this.support.id && b !== this.support.id)) {
          this.contacts.begin(a, b);
        }
        const runtimeA = this.animals.get(a);
        const runtimeB = this.animals.get(b);
        if (runtimeA && (b === this.support.id || runtimeB?.placed)) {
          runtimeA.hasEverContactedStack = true;
          this.beginStackPartContact(runtimeA, pair.collision.bodyA, pair.collision.bodyB);
        }
        if (runtimeB && (a === this.support.id || runtimeA?.placed)) {
          runtimeB.hasEverContactedStack = true;
          this.beginStackPartContact(runtimeB, pair.collision.bodyA, pair.collision.bodyB);
        }
      }
      this.updatePlacementFromCollision(event.timestamp);
    };
    const onActive = (event: Phaser.Physics.Matter.Events.CollisionActiveEvent) => {
      for (const pair of event.pairs) {
        const [a, b] = normalizeCollisionParents(pair.collision);
        if (!this.suppressSupportGraphForTest || (a !== this.support.id && b !== this.support.id)) {
          this.contacts.active(a, b);
        }
      }
      this.updatePlacementFromCollision(event.timestamp);
    };
    const onEnd = (event: Phaser.Physics.Matter.Events.CollisionEndEvent) => {
      for (const pair of event.pairs) {
        const [a, b] = normalizeCollisionParents(pair.collision);
        const runtimeA = this.animals.get(a);
        const runtimeB = this.animals.get(b);
        if (runtimeA && (b === this.support.id || runtimeB?.placed)) {
          this.endStackPartContact(runtimeA, pair.collision.bodyA, pair.collision.bodyB);
        }
        if (runtimeB && (a === this.support.id || runtimeA?.placed)) {
          this.endStackPartContact(runtimeB, pair.collision.bodyA, pair.collision.bodyB);
        }
        this.contacts.end(a, b);
      }
      this.updatePlacementFromCollision(event.timestamp);
    };
    const onAfterUpdate = (event: Phaser.Physics.Matter.Events.AfterUpdateEvent) => {
      this.updatePlacementFromCollision(event.timestamp);
      this.consumePendingPlacement();
    };
    MatterRuntime.Events.on(this.matter.world.engine, 'beforeSolve', onBeforeSolve);
    this.matter.world.on('collisionstart', onStart);
    this.matter.world.on('collisionactive', onActive);
    this.matter.world.on('collisionend', onEnd);
    this.matter.world.on('afterupdate', onAfterUpdate);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      MatterRuntime.Events.off(this.matter.world.engine, 'beforeSolve', onBeforeSolve);
      this.matter.world.off('collisionstart', onStart);
      this.matter.world.off('collisionactive', onActive);
      this.matter.world.off('collisionend', onEnd);
      this.matter.world.off('afterupdate', onAfterUpdate);
    });
  }

  private applyCurrentFallTerminalVelocity(): void {
    if (!this.current) return;
    const body = this.current.image.body as MatterJS.BodyType;
    const velocityY = gravityDirectionVelocityY({
      enabled: this.currentFallTerminalVelocityEnabled,
      isCurrent: true,
      dropped: this.current.dropped,
      placed: this.current.placed,
      hasEverContactedStack: this.current.hasEverContactedStack,
      dynamic: !body.isStatic,
      gravityY: this.matter.world.localWorld.gravity.y,
      velocityY: body.velocity.y,
      terminalSpeed: this.currentFallTerminalSpeed,
    });
    if (velocityY === body.velocity.y) return;
    this.matter.body.setVelocity(body, { x: body.velocity.x, y: velocityY });
  }

  private updatePlacementFromCollision(simulationTimestampMs: number): void {
    if (!this.current?.dropped || this.current.placed) return;
    const body = this.current.image.body as MatterJS.BodyType;
    const supportConnected = this.hasPlacementSupport(this.current, body.id);
    this.current.placement = updatePlacementContact(this.current.placement, {
      dropped: this.current.dropped,
      dynamic: !body.isStatic,
      placed: this.current.placed,
      supportConnected,
    }, simulationTimestampMs);
  }

  private hasPlacementSupport(runtime: AnimalRuntime, bodyId: number): boolean {
    return this.contacts.hasContact(this.support.id, bodyId)
      || runtime.activeStackPartContacts.size > 0;
  }

  private contactPartId(
    runtime: AnimalRuntime,
    bodyA: MatterJS.BodyType,
    bodyB: MatterJS.BodyType,
  ): string | null {
    const prefix = `animal:${runtime.definition.id}:`;
    for (const body of [bodyA, bodyB]) {
      if (body.label.startsWith(prefix)) return body.label.slice(prefix.length);
    }
    return null;
  }

  private beginStackPartContact(runtime: AnimalRuntime, bodyA: MatterJS.BodyType, bodyB: MatterJS.BodyType): void {
    const partId = this.contactPartId(runtime, bodyA, bodyB);
    if (!partId) return;
    runtime.contactedPartIds.add(partId);
    runtime.activeStackPartContacts.set(partId, (runtime.activeStackPartContacts.get(partId) ?? 0) + 1);
  }

  private endStackPartContact(runtime: AnimalRuntime, bodyA: MatterJS.BodyType, bodyB: MatterJS.BodyType): void {
    const partId = this.contactPartId(runtime, bodyA, bodyB);
    if (!partId) return;
    const nextCount = (runtime.activeStackPartContacts.get(partId) ?? 0) - 1;
    if (nextCount > 0) runtime.activeStackPartContacts.set(partId, nextCount);
    else runtime.activeStackPartContacts.delete(partId);
  }

  private consumePendingPlacement(): void {
    if (!this.current?.dropped || this.current.placed || !this.current.placement.pendingPlacement) return;
    const body = this.current.image.body as MatterJS.BodyType;
    const connected = this.contacts.connectedTo(this.support.id);
    if (!this.hasPlacementSupport(this.current, body.id)) {
      this.current.placement = createPlacementTracker();
      return;
    }
    this.current.placement = { ...this.current.placement, pendingPlacement: false };
    this.placeCurrent(connected);
  }

  private async startGame(): Promise<void> {
    await this.audio.unlock();
    this.resetWorldState();
    this.showGameplayUi();
    this.refreshViewLayout();
    this.spawnNext(true);
  }

  private resetWorldState(): void {
    for (const runtime of this.animals.values()) {
      runtime.image.destroy();
    }
    this.animals.clear();
    this.contacts.clear();
    this.current = null;
    this.latestAnimalBodyId = null;
    this.score = 0;
    this.currentRunMeters = 0;
    this.phase = 'aiming';
    this.previousBottomY = SUPPORT_Y + SUPPORT_HEIGHT / 2;
    this.cameraPlacedBottomY = this.previousBottomY;
    this.startOverviewActive = false;
    this.animalCursor = 0;
    this.animalSelector.reset();
    this.resetRotationKeyRepeat();
    this.gameOverReason = null;
    this.runtimeFailureReason = null;
    this.suppressSupportGraphForTest = false;
    this.placementTelemetry.length = 0;
    this.resetPlayCamera();
  }

  private resetPlayCamera(): void {
    this.cameraFrame = cameraFrameForZoom(1, this.supportAnchorWorldY, this.viewport);
    this.cameraTargetZoom = 1;
    this.cameraTransition = beginCameraTransition(1, 1, this.viewport);
    this.aimScreenX = this.viewport.canvasCenterX;
    this.applyCameraFrame();
  }

  private updatePlayCamera(delta: number): void {
    const advanced = advanceCameraTransition(this.cameraTransition, delta, this.supportAnchorWorldY, this.viewport);
    this.cameraTransition = advanced.transition;
    this.cameraFrame = advanced.frame;
    this.applyCameraFrame();
  }

  private applyCameraFrame(): void {
    this.cameras.main.setZoom(this.cameraFrame.zoom * this.renderPixelRatio).centerOn(SUPPORT_X, this.cameraFrame.centerY);
    // getWorldPoint reads the rendered camera matrix, so refresh it before anchoring the aiming body.
    this.cameras.main.preRender();
    this.syncSpawnLineGraphic();
  }

  private spawnNext(snapCameraToInitialFrame = false): void {
    if (this.phase === 'gameOver' || this.phase === 'runtimeFailure') return;
    const searchParams = new URLSearchParams(window.location.search);
    const requestedAnimalId = searchParams.has('e2e') && this.animalCursor === 0
      ? searchParams.get('animal')
      : null;
    const forcedDefinition = requestedAnimalId === null
      ? null
      : ANIMALS.find((animal) => animal.id === requestedAnimalId) ?? ANIMALS[0]!;
    const definition = forcedDefinition ?? this.animalSelector.next();
    this.animalCursor += 1;
    const spawnWorldPoint = this.cameras.main.getWorldPoint(
      this.aimScreenX * this.renderPixelRatio,
      this.viewport.spawnScreenY * this.renderPixelRatio,
    );
    const image = createAnimal(this, definition, spawnWorldPoint.x, spawnWorldPoint.y);
    if (new URLSearchParams(window.location.search).has('colliders')) {
      const render = (image.body as MatterJS.BodyType).render as unknown as {
        visible: boolean; lineColor: number; lineOpacity: number; lineThickness: number;
      };
      Object.assign(render, { visible: true, lineColor: 0xff00ff, lineOpacity: 1, lineThickness: 3 });
    }
    image.setDepth(10);
    const runtime: AnimalRuntime = {
      image,
      definition,
      normalFrictionAir: (image.body as MatterJS.BodyType).frictionAir,
      contactedPartIds: new Set(),
      activeStackPartContacts: new Map(),
      dropped: false,
      placed: false,
      placement: createPlacementTracker(),
      outMetadata: { hasEnteredEscapeCorridor: false },
      hasEverContactedStack: false,
      previousBounds: {
        minX: (image.body as MatterJS.BodyType).bounds.min.x,
        maxX: (image.body as MatterJS.BodyType).bounds.max.x,
        minY: (image.body as MatterJS.BodyType).bounds.min.y,
        maxY: (image.body as MatterJS.BodyType).bounds.max.y,
      },
    };
    this.animals.set((image.body as MatterJS.BodyType).id, runtime);
    this.latestAnimalBodyId = (image.body as MatterJS.BodyType).id;
    this.current = runtime;
    this.phase = 'aiming';
    this.startOverviewActive = snapCameraToInitialFrame;
    this.syncAimingBodyToScreenAnchor();
    this.retargetCameraForAimingClearance(snapCameraToInitialFrame);
    this.ui.setGameplayControlsEnabled(true);
    this.updateHud();
  }

  private showGameplayUi(): void {
    this.ui.showGame({
      rotateLeft: () => this.beginMobileRotation('left'),
      releaseRotateLeft: () => this.endMobileRotation('left'),
      rotateRight: () => this.beginMobileRotation('right'),
      releaseRotateRight: () => this.endMobileRotation('right'),
      drop: () => this.dropCurrent(),
    });
    this.ui.setHitboxDebugMode(this.hitboxDebugActive ? this.hitboxMode : null);
    this.ui.setGameplayControlsEnabled(this.phase === 'aiming' && Boolean(this.current));
  }

  private hitboxTargets() {
    return selectHitboxTargets(
      this.hitboxMode,
      this.current ? (this.current.image.body as MatterJS.BodyType).id : null,
      this.latestAnimalBodyId,
      [...this.animals.keys()],
    );
  }

  private drawHitboxBody(
    body: MatterJS.BodyType,
    fillColor: number,
    fillAlpha: number,
    strokeColor: number,
    strokeAlpha: number,
  ): void {
    if (!this.hitboxOverlay) return;
    const lineWidth = 2 / Math.max(this.cameraFrame.zoom, 0.001);
    for (const part of drawableHitboxParts(body)) {
      if (!part.vertices || part.vertices.length < 3) continue;
      const points = part.vertices.map(({ x, y }) => new Phaser.Math.Vector2(x, y));
      this.hitboxOverlay.fillStyle(fillColor, fillAlpha);
      this.hitboxOverlay.fillPoints(points, true);
      this.hitboxOverlay.lineStyle(lineWidth, strokeColor, strokeAlpha);
      this.hitboxOverlay.strokePoints(points, true, true);
    }
  }

  private drawHitboxOverlay(): void {
    if (!this.hitboxOverlay) return;
    this.hitboxOverlay.clear();
    if (this.hitboxMode === 'OFF') return;
    const targets = this.hitboxTargets();
    const currentStyle = this.hitboxMode === 'CURRENT';
    for (const bodyId of targets.animalIds) {
      const runtime = this.animals.get(bodyId);
      if (!runtime?.image.body) continue;
      this.drawHitboxBody(
        runtime.image.body as MatterJS.BodyType,
        0x25c76f,
        currentStyle ? 0.2 : 0.16,
        0x20e57b,
        currentStyle ? 0.85 : 0.7,
      );
      const body = runtime.image.body as MatterJS.BodyType;
      const centerOfMass = centerOfMassDebugPoint(body, runtime.definition);
      const radius = 5 / Math.max(this.cameraFrame.zoom, 0.001);
      this.hitboxOverlay.fillStyle(centerOfMass.color, 0.95);
      this.hitboxOverlay.fillCircle(centerOfMass.point.x, centerOfMass.point.y, radius);
      this.hitboxOverlay.lineStyle(1.5 / Math.max(this.cameraFrame.zoom, 0.001), 0x151515, 0.9);
      this.hitboxOverlay.strokeCircle(centerOfMass.point.x, centerOfMass.point.y, radius);
    }
    if (targets.includeSupport && this.support) {
      this.drawHitboxBody(this.support, 0x2388e8, 0.1, 0x2388e8, 0.55);
    }
  }

  private hitboxInspection(): DebugSnapshot['hitbox'] {
    const targets = this.hitboxTargets();
    const runtimes = targets.animalIds
      .map((id) => this.animals.get(id))
      .filter((runtime): runtime is AnimalRuntime => Boolean(runtime));
    return {
      mode: this.hitboxMode,
      animalIds: runtimes.map((runtime) => runtime.definition.id),
      partLabels: [...new Set(runtimes.flatMap((runtime) => drawableHitboxParts(
        runtime.image.body as MatterJS.BodyType,
      ).map((part) => part.label)))],
      includesSupport: targets.includeSupport,
      centerOfMassPoints: runtimes.map((runtime) => {
        const point = centerOfMassDebugPoint(runtime.image.body as MatterJS.BodyType, runtime.definition);
        return {
          animalId: runtime.definition.id,
          mode: point.mode,
          x: point.point.x,
          y: point.point.y,
        };
      }),
    };
  }

  private updateAim(delta: number): void {
    if (this.phase !== 'aiming' || !this.current) return;
    const distance = 0.32 * delta;
    if (this.keys.left.isDown) this.setAimScreenX(this.aimScreenX - distance);
    if (this.keys.right.isDown) this.setAimScreenX(this.aimScreenX + distance);
    const leftRepeat = advanceHeldKeyRepeat(this.rotateLeftRepeat, {
      isDown: this.keys.rotateLeft.isDown,
      justDown: Phaser.Input.Keyboard.JustDown(this.keys.rotateLeft),
      deltaMs: delta,
    });
    const rightRepeat = advanceHeldKeyRepeat(this.rotateRightRepeat, {
      isDown: this.keys.rotateRight.isDown,
      justDown: Phaser.Input.Keyboard.JustDown(this.keys.rotateRight),
      deltaMs: delta,
    });
    this.rotateLeftRepeat = leftRepeat.state;
    this.rotateRightRepeat = rightRepeat.state;
    const mobileLeftRepeat = advanceHeldKeyRepeat(this.mobileRotateLeftRepeat, {
      isDown: this.mobileRotateLeftHeld,
      justDown: false,
      deltaMs: delta,
    });
    const mobileRightRepeat = advanceHeldKeyRepeat(this.mobileRotateRightRepeat, {
      isDown: this.mobileRotateRightHeld,
      justDown: false,
      deltaMs: delta,
    });
    this.mobileRotateLeftRepeat = mobileLeftRepeat.state;
    this.mobileRotateRightRepeat = mobileRightRepeat.state;
    const rotationSteps = rightRepeat.steps - leftRepeat.steps
      + mobileRightRepeat.steps - mobileLeftRepeat.steps;
    if (rotationSteps !== 0) this.rotateCurrent(rotationSteps * 15);
    this.setAimScreenX(this.aimScreenX);
    if (Phaser.Input.Keyboard.JustDown(this.keys.drop)) this.dropCurrent();
  }

  private rotateCurrent(deltaDegrees: number): void {
    if (this.phase !== 'aiming' || !this.current) return;
    this.current.image.setAngle(this.current.image.angle + deltaDegrees);
    this.setAimScreenX(this.aimScreenX);
    this.retargetCameraForAimingClearance();
  }

  private setAimScreenX(x: number): void {
    if (!this.current || this.phase !== 'aiming') return;
    const body = this.current.image.body as MatterJS.BodyType;
    const aimOrigin = preservedAimOrigin(
      { x: this.current.image.originX, y: this.current.image.originY },
      centerOfMassAimOffset(body),
      { width: this.current.definition.width, height: this.current.definition.height },
    );
    this.aimScreenX = clampAimScreenX(
      x,
      this.current.definition.width,
      this.current.definition.height,
      this.current.image.angle,
      this.cameraFrame.zoom,
      this.viewport.stageWidth,
      aimOrigin.x,
      aimOrigin.y,
    );
    this.syncAimingBodyToScreenAnchor();
  }

  private syncAimingBodyToScreenAnchor(): void {
    if (!this.current || this.phase !== 'aiming' || this.current.dropped) return;
    const body = this.current.image.body as MatterJS.BodyType;
    const aimOffset = centerOfMassAimOffset(body);
    const bodyPositionFromAimAnchor = bodyPositionForPreservedAimAnchor(
      { x: 0, y: 0 },
      aimOffset,
      body.angle,
    );
    const lowerExtentWorld = Math.max(
      0,
      bodyPositionFromAimAnchor.y + body.bounds.max.y - body.position.y,
    );
    const lowestSafeAnchorY = this.viewport.stageHeight - MOBILE_AIMING_EDGE_GAP
      - lowerExtentWorld * this.cameraFrame.zoom;
    const anchorScreenY = this.viewport.layoutMode === 'desktop'
      ? this.viewport.spawnScreenY
      : Math.min(this.viewport.spawnScreenY, lowestSafeAnchorY);
    const worldPoint = this.cameras.main.getWorldPoint(
      this.aimScreenX * this.renderPixelRatio,
      anchorScreenY * this.renderPixelRatio,
    );
    const bodyPosition = bodyPositionForPreservedAimAnchor(
      worldPoint,
      aimOffset,
      body.angle,
    );
    this.matter.body.setPosition(body, bodyPosition);
    this.current.image.setPosition(bodyPosition.x, bodyPosition.y);
  }

  private dropCurrent(): void {
    if (!this.current || this.phase !== 'aiming') return;
    this.syncAimingBodyToScreenAnchor();
    const body = this.current.image.body as MatterJS.BodyType;
    this.current.previousBounds = {
      minX: body.bounds.min.x,
      maxX: body.bounds.max.x,
      minY: body.bounds.min.y,
      maxY: body.bounds.max.y,
    };
    this.current.dropped = true;
    this.current.image.setData('dropped', true);
    this.resetRotationKeyRepeat();
    enableAnimalCollisions(this.current.image);
    this.current.image.setStatic(false);
    this.current.image.setAwake();
    this.phase = 'falling';
    this.ui.setGameplayControlsEnabled(false);
    this.audio.play('drop');
    this.updateHud();
  }

  private resetRotationKeyRepeat(): void {
    this.rotateLeftRepeat = createHeldKeyRepeatState();
    this.rotateRightRepeat = createHeldKeyRepeatState();
    this.mobileRotateLeftRepeat = createHeldKeyRepeatState();
    this.mobileRotateRightRepeat = createHeldKeyRepeatState();
    this.mobileRotateLeftHeld = false;
    this.mobileRotateRightHeld = false;
  }

  private beginMobileRotation(direction: 'left' | 'right'): void {
    if (this.phase !== 'aiming' || !this.current) return;
    if (direction === 'left') {
      if (this.mobileRotateLeftHeld) return;
      this.mobileRotateLeftHeld = true;
      this.mobileRotateLeftRepeat = createHeldKeyRepeatState();
      this.rotateCurrent(-15);
      return;
    }
    if (this.mobileRotateRightHeld) return;
    this.mobileRotateRightHeld = true;
    this.mobileRotateRightRepeat = createHeldKeyRepeatState();
    this.rotateCurrent(15);
  }

  private endMobileRotation(direction: 'left' | 'right'): void {
    if (direction === 'left') {
      this.mobileRotateLeftHeld = false;
      this.mobileRotateLeftRepeat = createHeldKeyRepeatState();
      return;
    }
    this.mobileRotateRightHeld = false;
    this.mobileRotateRightRepeat = createHeldKeyRepeatState();
  }

  private updatePhysicsState(deltaMs: number): void {
    this.attenuateIndirectImpactTransmission(deltaMs);
    const connected = this.contacts.connectedTo(this.support.id);
    const supportBounds = {
      minX: this.support.bounds.min.x,
      maxX: this.support.bounds.max.x,
      minY: this.support.bounds.min.y,
      maxY: this.support.bounds.max.y,
    };

    for (const [bodyId, runtime] of this.animals) {
      if (!runtime.dropped) continue;
      const body = runtime.image.body as MatterJS.BodyType;
      const supportConnected = connected.has(bodyId);
      const currentBounds = {
        minX: body.bounds.min.x,
        maxX: body.bounds.max.x,
        minY: body.bounds.min.y,
        maxY: body.bounds.max.y,
      };
      if (!hasFinitePhysicsState({
        positionX: body.position.x,
        positionY: body.position.y,
        velocityX: body.velocity.x,
        velocityY: body.velocity.y,
        boundsMinX: body.bounds.min.x,
        boundsMaxX: body.bounds.max.x,
        boundsMinY: body.bounds.min.y,
        boundsMaxY: body.bounds.max.y,
      })) {
        this.endRuntimeFailure('非有限の物理状態を検出しました', 'non-finite');
        return;
      }
      if (shouldWakeUnsupportedDropped({
        dropped: runtime.dropped,
        placed: runtime.placed,
        dynamic: !body.isStatic,
        supportConnected,
        sleeping: body.isSleeping,
        hasEnteredEscapeCorridor: runtime.outMetadata.hasEnteredEscapeCorridor,
      })) {
        runtime.image.setAwake();
      }
      runtime.outMetadata = updateEscapeMetadata({
        minX: body.bounds.min.x, maxX: body.bounds.max.x, minY: body.bounds.min.y,
      }, supportBounds, runtime.outMetadata);

      if (detectSupportPenetration(runtime.previousBounds, currentBounds, supportBounds, this.contacts.hasContact(this.support.id, bodyId))) {
        console.error('reverse-animal-tower: support penetration invariant', {
          bodyId,
          animalId: runtime.definition.id,
          bodyBounds: body.bounds,
          supportBounds,
          supportContact: this.contacts.hasContact(this.support.id, bodyId),
          supportConnected,
        });
        this.endRuntimeFailure('支持面の物理境界異常を検出しました', 'support-penetration');
        return;
      }
      runtime.previousBounds = currentBounds;

      if (isOut({
        dropped: true,
        dynamic: !body.isStatic,
        supportConnected,
        boundsMaxY: body.bounds.max.y,
        outLineY: OUT_LINE_Y,
        hasEnteredEscapeCorridor: runtime.outMetadata.hasEnteredEscapeCorridor,
        hasEverContactedStack: runtime.hasEverContactedStack,
      })) {
        this.endGame('どうぶつが上へ落ちました', runtime.hasEverContactedStack ? 'collapse-out' : 'direct-miss-out');
        return;
      }
    }
    this.updateSettledStackProgress();
  }

  private attenuateIndirectImpactTransmission(deltaMs: number): void {
    if (!this.current?.dropped || this.current.placed) return;
    const currentBody = this.current.image.body as MatterJS.BodyType;
    const distances = this.contacts.distancesFrom(currentBody.id, new Set([this.support.id]));
    for (const [bodyId, runtime] of this.animals) {
      if (!runtime.placed) continue;
      const distance = distances.get(bodyId);
      if (distance === undefined || distance <= 1) continue;
      const retention = indirectImpactRetention(distance, deltaMs);
      const body = runtime.image.body as MatterJS.BodyType;
      this.matter.body.setVelocity(body, {
        x: body.velocity.x * retention,
        y: body.velocity.y * retention,
      });
      this.matter.body.setAngularVelocity(body, body.angularVelocity * retention);
      body.positionImpulse.x *= retention;
      body.positionImpulse.y *= retention;
    }
  }

  private updateSettledStackProgress(): void {
    const snapshots = [...this.animals.values()].map((runtime) => {
      const body = runtime.image.body as MatterJS.BodyType;
      const settled = runtime.placed && body.isSleeping && !runtime.outMetadata.hasEnteredEscapeCorridor;
      return {
        bottomY: body.bounds.max.y,
        placed: settled,
        supportConnected: settled,
      };
    });
    const settledBottomY = placedStackBottom(snapshots, SUPPORT_Y + SUPPORT_HEIGHT / 2);
    if (settledBottomY <= this.cameraPlacedBottomY + 1e-6) return;

    this.cameraPlacedBottomY = settledBottomY;
    const measured = measureStack(snapshots, this.supportAnchorWorldY);
    this.currentRunMeters = updateRunMeters(this.currentRunMeters, measured.meters);
    const nextBestMeters = Math.max(this.bestMeters, this.currentRunMeters);
    if (nextBestMeters !== this.bestMeters) {
      const saved = this.bestStore.save({ bestPoint: this.bestPoint, bestMeters: nextBestMeters });
      this.bestPoint = saved.bestPoint;
      this.bestMeters = saved.bestMeters;
    }
    if (this.phase === 'aiming' && this.current && !this.current.dropped) {
      this.setCameraTargetForPlacedBottom(settledBottomY);
    }
    this.updateHud();
  }

  private placeCurrent(connected: Set<number>): void {
    if (!this.current || this.current.placed) return;
    this.current.placed = true;
    this.current.image.setData('placed', true);
    const placedBody = this.current.image.body as MatterJS.BodyType;
    const placementSupported = this.hasPlacementSupport(this.current, placedBody.id);
    placedBody.frictionAir = this.current.normalFrictionAir * PLACED_FRICTION_AIR_MULTIPLIER;
    placedBody.sleepThreshold = PLACED_SLEEP_THRESHOLD;
    this.placementTelemetry.push({
      id: this.current.definition.id,
      contactPartIds: [...this.current.activeStackPartContacts.keys()].sort(),
      connectedDurationMs: this.current.placement.connectedDurationMs,
      speed: placedBody.speed,
      angularSpeed: placedBody.angularSpeed,
      sleeping: placedBody.isSleeping,
      supportConnected: placementSupported,
    });
    const snapshots = [...this.animals.entries()].map(([id, runtime]) => ({
      bottomY: (runtime.image.body as MatterJS.BodyType).bounds.max.y,
      placed: runtime.placed,
      supportConnected: id === placedBody.id ? placementSupported : connected.has(id),
    }));
    const currentBottomY = placedStackBottom(snapshots, SUPPORT_Y + SUPPORT_HEIGHT / 2);
    const placement = calculatePlacementScore(this.current.definition.baseScore, this.previousBottomY, currentBottomY);
    this.previousBottomY = currentBottomY;
    this.score += placement.total;
    const nextBestPoint = Math.min(this.bestPoint, this.score);
    const nextBestMeters = Math.max(this.bestMeters, this.currentRunMeters);
    if (nextBestPoint !== this.bestPoint || nextBestMeters !== this.bestMeters) {
      const saved = this.bestStore.save({ bestPoint: nextBestPoint, bestMeters: nextBestMeters });
      this.bestPoint = saved.bestPoint;
      this.bestMeters = saved.bestMeters;
    }
    this.ui.showPlacement(placement.total);
    this.audio.play('placed');
    this.current = null;
    this.phase = 'aiming';
    this.spawnNext();
  }

  private updateHud(): void {
    this.ui.updateHud({
      currentPoint: this.score,
      currentMeters: this.currentRunMeters,
      bestPoint: this.bestPoint,
      bestMeters: this.bestMeters,
      animalName: this.current?.definition.name ?? '',
    });
  }

  private endGame(copy: string, reason: string): void {
    if (this.phase === 'gameOver') return;
    this.phase = 'gameOver';
    this.gameOverReason = reason;
    this.audio.play(reason === 'collapse-out' ? 'collapse' : 'gameOver');
    this.ui.showGameOver(
      this.score,
      this.currentRunMeters,
      { bestPoint: this.bestPoint, bestMeters: this.bestMeters },
      copy,
      () => { void this.retry(); },
    );
    this.refreshViewLayout();
  }

  private endRuntimeFailure(copy: string, reason: string): void {
    if (this.phase === 'runtimeFailure') return;
    this.phase = 'runtimeFailure';
    this.runtimeFailureReason = reason;
    this.gameOverReason = null;
    this.ui.showRuntimeFailure(copy, () => { void this.retry(); });
    this.refreshViewLayout();
  }

  private async retry(): Promise<void> {
    await this.audio.unlock();
    this.resetWorldState();
    this.showGameplayUi();
    this.refreshViewLayout();
    this.spawnNext(true);
  }

  private installDebugApi(): void {
    window.__REVERSE_ANIMAL_TOWER__ = {
      snapshot: () => this.debugSnapshot(),
      setAim: (x, angle) => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return;
        this.current?.image.setAngle(angle);
        this.setAimScreenX(x);
        this.retargetCameraForAimingClearance();
      },
      drop: () => {
        if (new URLSearchParams(window.location.search).has('e2e')) this.dropCurrent();
      },
      setCurrentAngularVelocityForTest: (velocity) => {
        if (!new URLSearchParams(window.location.search).has('e2e') || !this.current?.dropped) return;
        this.matter.body.setAngularVelocity(this.current.image.body as MatterJS.BodyType, velocity);
      },
      textureExistsForTest: (key) => (
        new URLSearchParams(window.location.search).has('e2e') && this.textures.exists(key)
      ),
      setCurrentParentBoundsMaxYForTest: (maxY) => {
        if (!new URLSearchParams(window.location.search).has('e2e') || !this.current?.dropped) return;
        const body = this.current.image.body as MatterJS.BodyType;
        this.matter.body.setPosition(body, { x: body.position.x, y: body.position.y + maxY - body.bounds.max.y });
        this.current.previousBounds = {
          minX: body.bounds.min.x,
          maxX: body.bounds.max.x,
          minY: body.bounds.min.y,
          maxY: body.bounds.max.y,
        };
      },
      currentOutForTest: () => {
        if (!new URLSearchParams(window.location.search).has('e2e') || !this.current) return false;
        const body = this.current.image.body as MatterJS.BodyType;
        return isOut({
          dropped: this.current.dropped,
          dynamic: !body.isStatic,
          supportConnected: this.contacts.connectedTo(this.support.id).has(body.id),
          boundsMaxY: body.bounds.max.y,
          outLineY: OUT_LINE_Y,
        });
      },
      destabilizePlaced: () => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return;
        const placed = [...this.animals.values()].find((runtime) => runtime.placed);
        if (!placed) return;
        const body = placed.image.body as MatterJS.BodyType;
        placed.image.setAwake();
        this.matter.body.setVelocity(body, { x: 22, y: 8 });
        this.matter.body.setAngularVelocity(body, 0.08);
      },
      forceSleepCurrent: () => {
        if (!new URLSearchParams(window.location.search).has('e2e') || !this.current?.dropped) return;
        MatterRuntime.Sleeping.set(this.current.image.body as MatterJS.BodyType, true);
      },
      forceSleepPlaced: () => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return;
        const placed = [...this.animals.values()].find((runtime) => runtime.placed);
        if (placed) MatterRuntime.Sleeping.set(placed.image.body as MatterJS.BodyType, true);
      },
      forceSweptSupportCrossing: () => {
        if (!new URLSearchParams(window.location.search).has('e2e') || !this.current?.dropped) return;
        const body = this.current.image.body as MatterJS.BodyType;
        this.current.previousBounds = {
          minX: body.bounds.min.x,
          maxX: body.bounds.max.x,
          minY: body.bounds.min.y,
          maxY: body.bounds.max.y,
        };
        this.matter.body.setPosition(body, { x: SUPPORT_X, y: -80 });
      },
      setPhysicsPausedForTest: (paused) => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return;
        if (paused) this.matter.world.pause();
        else this.matter.world.resume();
      },
      disconnectSupportGraphForTest: () => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return;
        this.suppressSupportGraphForTest = true;
        this.contacts.removeBody(this.support.id);
      },
      setPlacedCameraBottomForTest: (bottomY) => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return;
        this.cameraPlacedBottomY = bottomY;
        this.setCameraTargetForPlacedBottom(bottomY, true);
      },
      launchPlacedThroughAiming: () => {
        if (!new URLSearchParams(window.location.search).has('e2e') || !this.current || this.current.dropped) return;
        const placed = [...this.animals.values()].find((runtime) => runtime.placed);
        if (!placed) return;
        const aimingBody = this.current.image.body as MatterJS.BodyType;
        const body = placed.image.body as MatterJS.BodyType;
        const targetMinY = aimingBody.bounds.max.y + 12;
        const deltaY = targetMinY - body.bounds.min.y;
        placed.image.setAwake();
        this.matter.body.setPosition(body, { x: aimingBody.position.x, y: body.position.y + deltaY });
        this.matter.body.setVelocity(body, { x: 0, y: -18 });
        placed.previousBounds = {
          minX: body.bounds.min.x,
          maxX: body.bounds.max.x,
          minY: body.bounds.min.y,
          maxY: body.bounds.max.y,
        };
      },
      physicsTelemetry: () => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return null;
        const engine = this.matter.world.engine as unknown as {
          timing: { timestamp: number };
          pairs: { list: PhysicsPairTelemetryInput[] };
        };
        return snapshotPhysicsTelemetry(
          engine.timing.timestamp,
          [...this.animals.values()].map((runtime) => ({
            animalId: runtime.definition.id,
            body: runtime.image.body as MatterJS.BodyType,
          })),
          engine.pairs.list,
        );
      },
      inspectRoster: () => {
        if (!new URLSearchParams(window.location.search).has('e2e')) return [];
        return ANIMALS.map((definition) => {
          const collisionParts = collisionPartsForAnimal(definition);
          const image = createAnimal(this, definition, SUPPORT_X, 360);
          image.setStatic(false);
          const body = image.body as MatterJS.BodyType;
          let maximumLowerExtent = 0;
          for (let angle = 0; angle < 360; angle += 15) {
            image.setAngle(angle);
            maximumLowerExtent = Math.max(maximumLowerExtent, body.bounds.max.y - body.position.y);
          }
          image.setAngle(0);
          const textureAnchorX = image.x + definition.width * (0.5 - image.originX);
          const textureAnchorY = image.y + definition.height * (0.5 - image.originY);
          const maskPoints = collisionParts.flatMap((part) => part.kind === 'polygon'
            ? part.vertices.map((vertex) => ({ x: part.x + vertex.x, y: part.y + vertex.y }))
            : []);
          const maskAligned = definition.hitboxMask && maskPoints.length > 0
            ? Math.abs(body.bounds.min.x - (textureAnchorX + Math.min(...maskPoints.map(({ x }) => x)))) < 0.02
              && Math.abs(body.bounds.max.x - (textureAnchorX + Math.max(...maskPoints.map(({ x }) => x)))) < 0.02
              && Math.abs(body.bounds.min.y - (textureAnchorY + Math.min(...maskPoints.map(({ y }) => y)))) < 0.02
              && Math.abs(body.bounds.max.y - (textureAnchorY + Math.max(...maskPoints.map(({ y }) => y)))) < 0.02
            : null;
          const inspection: CompoundInspection = {
            id: definition.id,
            partCount: body.parts.length,
            expectedPartCount: definition.hitboxMask ? body.parts.length : collisionParts.length + 1,
            mass: body.mass,
            inertia: body.inertia,
            bounds: {
              minX: body.bounds.min.x,
              maxX: body.bounds.max.x,
              minY: body.bounds.min.y,
              maxY: body.bounds.max.y,
            },
            maximumLowerExtent,
            textureSourceWidth: image.texture.source[0]?.width ?? 0,
            textureSourceHeight: image.texture.source[0]?.height ?? 0,
            frameWidth: image.frame.width,
            frameHeight: image.frame.height,
            textureFilter: image.texture.source[0]?.scaleMode === Phaser.ScaleModes.LINEAR
              ? 'LINEAR'
              : image.texture.source[0]?.scaleMode === Phaser.ScaleModes.NEAREST ? 'NEAREST' : 'UNKNOWN',
            partLabels: body.parts.slice(1).map((part) => part.label),
            partBounds: body.parts.slice(1).map((part) => ({
              label: part.label,
              minX: part.bounds.min.x,
              maxX: part.bounds.max.x,
              minY: part.bounds.min.y,
              maxY: part.bounds.max.y,
            })),
            aligned: maskAligned ?? collisionParts.every((part, index) => {
              const bodyPart = body.parts[index + 1];
              if (!bodyPart) return false;
              const partAnchorX = textureAnchorX + part.x;
              const partAnchorY = textureAnchorY + part.y;
              return Math.abs(bodyPart.position.x - partAnchorX) < 0.01
                && Math.abs(bodyPart.position.y - partAnchorY) < 0.01;
            }),
          };
          image.destroy();
          return inspection;
        });
      },
    };
  }

  private debugSnapshot(): DebugSnapshot {
    const connected = this.contacts.connectedTo(this.support.id);
    const aimingBodyId = this.current && !this.current.dropped
      ? (this.current.image.body as MatterJS.BodyType).id
      : null;
    return {
      phase: this.phase,
      score: this.score,
      best: this.bestPoint,
      currentRunMeters: this.currentRunMeters,
      bestPoint: this.bestPoint,
      bestMeters: this.bestMeters,
      gravityY: this.matter.world.localWorld.gravity.y,
      physicsPreset: activePhysicsIsolationPreset(),
      gameOverReason: this.gameOverReason,
      runtimeFailureReason: this.runtimeFailureReason,
      cameraZoom: this.cameraFrame.zoom,
      cameraCenterY: this.cameraFrame.centerY,
      cameraTargetZoom: this.cameraTargetZoom,
      cameraTargetCenterY: cameraFrameForZoom(this.cameraTargetZoom, this.supportAnchorWorldY, this.viewport).centerY,
      aimingCameraEnvelope: { ...this.aimingCameraEnvelope },
      viewport: { ...this.viewport },
      renderer: this.inspectRenderer(),
      backdropBounds: { ...this.backdropBounds },
      hitbox: this.hitboxInspection(),
      aimingContactCount: aimingBodyId === null
        ? 0
        : [...this.animals.keys()].filter((id) => id !== aimingBodyId && this.contacts.hasContact(aimingBodyId, id)).length,
      placementTelemetry: this.placementTelemetry.map((sample) => ({ ...sample })),
      animals: [...this.animals.entries()].map(([id, runtime]) => ({
        bodyX: (runtime.image.body as MatterJS.BodyType).position.x,
        bodyY: (runtime.image.body as MatterJS.BodyType).position.y,
        bodyAngle: (runtime.image.body as MatterJS.BodyType).angle,
        velocityY: (runtime.image.body as MatterJS.BodyType).velocity.y,
        speed: (runtime.image.body as MatterJS.BodyType).speed,
        angularSpeed: (runtime.image.body as MatterJS.BodyType).angularSpeed,
        frictionAir: (runtime.image.body as MatterJS.BodyType).frictionAir,
        sleepThreshold: (runtime.image.body as MatterJS.BodyType).sleepThreshold,
        sleeping: (runtime.image.body as MatterJS.BodyType).isSleeping,
        mass: (runtime.image.body as MatterJS.BodyType).mass,
        inertia: (runtime.image.body as MatterJS.BodyType).inertia,
        boundsMinY: (runtime.image.body as MatterJS.BodyType).bounds.min.y,
        boundsMaxY: (runtime.image.body as MatterJS.BodyType).bounds.max.y,
        collisionMask: (runtime.image.body as MatterJS.BodyType).collisionFilter.mask,
        originX: runtime.image.originX,
        originY: runtime.image.originY,
        displayWidth: runtime.image.displayWidth,
        displayHeight: runtime.image.displayHeight,
        id: runtime.definition.id,
        x: runtime.image.x,
        y: runtime.image.y,
        angle: runtime.image.angle,
        visible: runtime.image.visible,
        textureKey: runtime.image.texture.key,
        contactedPartIds: [...runtime.contactedPartIds].sort(),
        activeContactCount: this.contacts.connectedTo((runtime.image.body as MatterJS.BodyType).id).size,
        placed: runtime.placed,
        dropped: runtime.dropped,
        supportConnected: runtime.dropped && !runtime.placed
          ? this.hasPlacementSupport(runtime, id)
          : connected.has(id),
        placementConnectedDurationMs: runtime.placement.connectedDurationMs,
        pendingPlacement: runtime.placement.pendingPlacement,
      })),
    };
  }

  private inspectRenderer(): DebugSnapshot['renderer'] {
    if (this.game.renderer.type !== Phaser.WEBGL) {
      return {
        type: this.game.renderer.type === Phaser.CANVAS ? 'CANVAS' : 'UNKNOWN',
        mipmapFilter: 'UNAVAILABLE',
      };
    }
    const renderer = this.game.renderer as Phaser.Renderer.WebGL.WebGLRenderer;
    return {
      type: 'WEBGL',
      mipmapFilter: renderer.mipmapFilter === renderer.gl.LINEAR_MIPMAP_LINEAR
        ? 'LINEAR_MIPMAP_LINEAR'
        : String(renderer.mipmapFilter),
    };
  }

  private updateDebugAttribute(): void {
    if (!this.hitboxDebugActive) return;
    document.querySelector('#game-ui')?.setAttribute('data-debug-snapshot', JSON.stringify(this.debugSnapshot()));
  }

  private teardown(): void {
    this.stageResizeObserver?.disconnect();
    this.stageResizeObserver = null;
    window.visualViewport?.removeEventListener('resize', this.scheduleViewportRefresh);
    if (this.viewportResizeFrame !== null) cancelAnimationFrame(this.viewportResizeFrame);
    this.viewportResizeFrame = null;
    if (this.hitboxDebugActive) this.input.keyboard?.off('keydown-H', this.toggleHitboxMode);
    this.hitboxOverlay?.destroy();
    this.hitboxOverlay = null;
    this.contacts.clear();
    this.animals.clear();
    delete window.__REVERSE_ANIMAL_TOWER__;
    this.ui.clear();
  }

  private get supportAnchorWorldY(): number {
    return this.support.bounds.max.y;
  }

  private measureMaximumRosterAimingEnvelope(): {
    lowerExtentWorld: number;
    topExtentWorld: number;
    heightWorld: number;
  } {
    let lowerExtentWorld = 0;
    let topExtentWorld = 0;
    let heightWorld = 0;
    for (const definition of ANIMALS) {
      const image = createAnimal(this, definition, SUPPORT_X, 360);
      const body = image.body as MatterJS.BodyType;
      const aimOffset = centerOfMassAimOffset(body);
      for (let angle = 0; angle < 360; angle += 15) {
        image.setAngle(angle);
        const bodyPositionFromAimAnchor = bodyPositionForPreservedAimAnchor(
          { x: 0, y: 0 },
          aimOffset,
          body.angle,
        );
        lowerExtentWorld = Math.max(
          lowerExtentWorld,
          bodyPositionFromAimAnchor.y + body.bounds.max.y - body.position.y,
        );
        topExtentWorld = Math.max(topExtentWorld, body.position.y - body.bounds.min.y);
        heightWorld = Math.max(heightWorld, body.bounds.max.y - body.bounds.min.y);
      }
      image.destroy();
    }
    return { lowerExtentWorld, topExtentWorld, heightWorld };
  }

  private refreshViewLayout(): void {
    const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const layoutMode = layoutModeForViewport(viewportWidth, viewportHeight);
    this.ui.setViewportLayout(layoutMode);
    const stage = document.querySelector<HTMLElement>('#game-stage');
    if (!stage) return;
    const stageBounds = stage.getBoundingClientRect();
    const previousViewport = this.viewport;
    const nextViewport = viewportMetricsForStage(
      layoutMode,
      stageBounds.width,
      stageBounds.height,
      this.maximumRosterAimingLowerExtent,
    );
    const relativeAimX = previousViewport.stageWidth > 0 ? this.aimScreenX / previousViewport.stageWidth : 0.5;
    this.viewport = nextViewport;
    this.redrawBackdropForViewport(this.supportAnchorWorldY);
    this.renderPixelRatio = renderPixelRatioForDevice(window.devicePixelRatio);
    this.aimScreenX = Math.min(nextViewport.stageWidth, Math.max(0, relativeAimX * nextViewport.stageWidth));
    const backingWidth = nextViewport.stageWidth * this.renderPixelRatio;
    const backingHeight = nextViewport.stageHeight * this.renderPixelRatio;
    if (this.scale.width !== backingWidth || this.scale.height !== backingHeight) {
      this.scale.setGameSize(backingWidth, backingHeight);
    }
    this.scale.refresh();
    if (this.phase !== 'gameOver' && this.phase !== 'runtimeFailure') {
      this.cameraFrame = cameraFrameForZoom(this.cameraFrame.zoom, this.supportAnchorWorldY, this.viewport);
    }
    this.applyCameraFrame();
    if (this.phase === 'aiming' && this.current) {
      this.setAimScreenX(this.aimScreenX);
      this.retargetCameraForAimingClearance();
    }
    this.syncSpawnLineLayout();
  }

  private setCameraTargetForPlacedBottom(
    bottomY: number,
    replaceTarget = false,
    snapToTarget = false,
  ): void {
    const aimingTopExtentWorld = this.aimingTopExtentWorld;
    const requiredZoom = cameraTargetZoomForPlacedBottom(
      bottomY,
      this.supportAnchorWorldY,
      aimingTopExtentWorld,
      this.viewport,
      this.aimingHeightWorld,
    );
    const startOverviewZoom = this.startOverviewActive
      ? cameraZoomToFitWorldWidth(SUPPORT_WIDTH, this.viewport)
      : 1;
    const framedZoom = Math.min(requiredZoom, startOverviewZoom);
    const nextTarget = replaceTarget || this.startOverviewActive
      ? framedZoom
      : Math.min(this.cameraTargetZoom, framedZoom);
    if (snapToTarget) {
      this.cameraTargetZoom = nextTarget;
      this.cameraFrame = cameraFrameForZoom(nextTarget, this.supportAnchorWorldY, this.viewport);
      this.cameraTransition = beginCameraTransition(nextTarget, nextTarget, this.viewport);
      this.applyCameraFrame();
      return;
    }
    if (Math.abs(nextTarget - this.cameraTargetZoom) < 1e-9) return;
    this.cameraTargetZoom = nextTarget;
    this.cameraTransition = beginCameraTransition(this.cameraFrame.zoom, this.cameraTargetZoom, this.viewport);
  }

  private retargetCameraForAimingClearance(snapToTarget = false): void {
    if (!this.current || this.current.dropped) return;
    this.syncAimingBodyToScreenAnchor();
    this.setCameraTargetForPlacedBottom(
      this.cameraPlacedBottomY,
      snapToTarget,
      snapToTarget,
    );
    if (snapToTarget) this.syncAimingBodyToScreenAnchor();
  }

  private get aimingTopExtentWorld(): number {
    if (!this.current || this.current.dropped) return 0;
    return this.aimingCameraEnvelope.topExtentWorld;
  }

  private get aimingHeightWorld(): number {
    if (!this.current || this.current.dropped) return 0;
    return this.aimingCameraEnvelope.heightWorld;
  }
}
